import { createAttributeApprovalTicket, createOrUpdateAttributeDetails } from 'src/graphql/mutations';
import { listAttributeDetails } from 'src/graphql/queries';
import { fetchGraphQLRequest } from 'src/services/api/clients/graphqlClient';
import {
  AttributeDetails,
  AttributeDetailsInput,
  AttributeDetailsResponse,
  CreateAttributeApprovalTicketInput,
  TicketCreationResponse,
} from 'src/types/attributes/attributes.types';
import { formatApiError } from 'src/utils/errorUtils';
import { logger } from 'src/utils/logger';
import { fetchAllPaginatedData } from 'src/utils/paginationUtils';

export const fetchAttributesAPI = async (): Promise<AttributeDetails[]> => {
  try {
    const rawData = await fetchAllPaginatedData<AttributeDetails, AttributeDetailsResponse>(
      async (page, pageSize) => {
        const result = await fetchGraphQLRequest<{ listAttributeDetails: AttributeDetailsResponse }>({
          query: listAttributeDetails,
          variables: {
            pagination: { page, page_size: pageSize },
          },
          requestName: 'listAttributeDetails',
          requestType: 'query',
        });
        return result.listAttributeDetails;
      },
      { logPrefix: 'Attribute details', pageSize: 50 }
    );

    return rawData;
  } catch (error) {
    logger.error('Error fetching Attribute details from GraphQL API:', { error });
    throw formatApiError('Failed to fetch Attribute details', error);
  }
};

export const updateAttributesAPI = async (
  mutationData: AttributeDetailsInput
): Promise<{ success: boolean; message: string }> => {
  try {
    logger.info(`Updating attributes to GraphQL API: ${mutationData}`, mutationData);
    const result = await fetchGraphQLRequest<{ createOrUpdateAttributeDetails: { numberOfRecordsUpdated: number } }>({
      query: createOrUpdateAttributeDetails,
      variables: { input: mutationData },
      requestName: 'createOrUpdateAttributeDetails',
      requestType: 'mutation',
    });

    const numberOfRecordsUpdated = result.createOrUpdateAttributeDetails.numberOfRecordsUpdated || 0;
    logger.info(`Attributes updated successfully`, { numberOfRecordsUpdated });
    return {
      success: numberOfRecordsUpdated > 0,
      message: `Attributes updated successfully (${numberOfRecordsUpdated} records updated)`,
    };
  } catch (error) {
    logger.error('Error updating attributes from GraphQL API:', { error });
    throw formatApiError('Failed to update attributes', error);
  }
};

export const createAttributeApprovalTicketAPI = async (
  input: CreateAttributeApprovalTicketInput
): Promise<TicketCreationResponse> => {
  try {
    logger.info('Creating attribute approval ticket', { input });
    const result = await fetchGraphQLRequest<{
      createAttributeApprovalTicket: TicketCreationResponse;
    }>({
      query: createAttributeApprovalTicket,
      variables: { input },
      requestName: 'createAttributeApprovalTicket',
      requestType: 'mutation',
    });

    const ticketId = result.createAttributeApprovalTicket.ticket_id;
    logger.info('Attribute approval ticket created successfully', { ticketId });
    return result.createAttributeApprovalTicket;
  } catch (error) {
    logger.error('Error creating attribute approval ticket:', { error });
    throw formatApiError('Failed to create attribute approval ticket', error);
  }
};

export const attributesService = {
  fetchAttributesAPI,
  updateAttributesAPI,
  createAttributeApprovalTicketAPI,
};
